import type { FC } from 'react';
import { Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@ui/table.tsx';
import { Button } from '@ui/button.tsx';
import type { RewardGroupedRewardReadModel } from '@r/api/generated/client.schemas.ts';

interface StepRewardsTableProps {
  rewards: RewardGroupedRewardReadModel[];
  onEditReward?: (reward: RewardGroupedRewardReadModel) => void;
}

const emptyCell = '—';

/** Lignes typologie / description / produit pour un palier donné. */
export const StepRewardsTable: FC<StepRewardsTableProps> = ({ rewards, onEditReward }) => {
  const { t } = useTranslation();

  const sortedRewards = [...rewards].sort((a, b) =>
    (a.typology?.name ?? '').localeCompare(b.typology?.name ?? '', 'fr')
  );

  return (
    <div className="overflow-hidden rounded-md border">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50">
            <TableHead>{t('admin.settings.steps.rewards.column.typology')}</TableHead>
            <TableHead>{t('admin.settings.steps.rewards.column.description')}</TableHead>
            <TableHead>{t('admin.settings.steps.rewards.column.product')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedRewards.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3} className="text-muted-foreground">
                {t('admin.settings.steps.rewards.empty')}
              </TableCell>
            </TableRow>
          ) : (
            sortedRewards.map((reward) => {
              const productDescription = reward.product?.description?.trim();
              const productName = reward.product?.name?.trim();
              const rowKey = reward.id ?? `${reward.typology?.name}-${productName}`;
              const canEdit = reward.id != null && onEditReward != null;

              return (
                <TableRow key={rowKey}>
                  <TableCell className="font-medium">
                    {reward.typology?.name ?? emptyCell}
                  </TableCell>
                  <TableCell className="max-w-[240px] truncate text-muted-foreground">
                    {productDescription || emptyCell}
                  </TableCell>
                  <TableCell className="max-w-[280px]">
                    <div className="flex gap-2 justify-between items-center">
                      <span className="truncate">{productName || emptyCell}</span>
                      {canEdit ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="shrink-0 text-muted-foreground"
                          aria-label={t('admin.settings.steps.rewards.action.edit')}
                          onClick={() => onEditReward(reward)}
                        >
                          <Pencil className="size-4" />
                        </Button>
                      ) : null}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
};
